import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useDebouncedCallback } from 'use-debounce'
import type { Artist, ArtistId } from 'types/artist'
import type { ClientLibraryArtist } from 'types/client-library'
import type { State } from 'types/redux'
import type { DropdownOption } from 'types/ui'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  loadRelatedArtists,
  updateArtist,
} from '@disco/state-management/actions/artist-actions'
import api from '@disco/api/server'
import { getRelatedArtistsDropdownOptions } from '@disco/state-management/selectors/artists'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import Row from 'components/common/row'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
type Props = {
  artist: Artist | ClientLibraryArtist
}

const ArtistRelatedArtists = ({ artist }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const relatedArtistsDropdownOptions: DropdownOption[] = useSelector(
    (state: State) => getRelatedArtistsDropdownOptions(state, artist.id),
  )
  const typeData = useComposerInsteadOfArtist().value
  const relatedArtistsIDs: ArtistId[] | null | undefined =
    artist.related_artist_ids
  useEffect(() => {
    if (relatedArtistsIDs && relatedArtistsIDs.length > 0) {
      dispatch(loadRelatedArtists(artist.id))
    }
  }, [relatedArtistsIDs])
  let abortController: AbortController | null | undefined
  const getArtistOptions = useDebouncedCallback(
    async (
      input: string,
      callback: (error: Error | null | undefined, result?: any) => void,
    ) => {
      try {
        if (abortController) {
          abortController.abort()
        }

        abortController = new AbortController()
        const response = await api.getArtists(
          input,
          'exclude_master_artists',
          abortController.signal,
        )
        // TODO: Cache returned results in store. Using dispatch here is causing rerenders.
        const artists = response.results.filter(
          (item: Artist) => item.id !== artist.id,
        )
        const result = {
          options: artists.map((artist: Artist) => ({
            label: artist.name,
            value: artist.id,
          })),
          complete: false,
        }
        callback(null, result)
      } catch (error) {
        callback(error)
      }
    },
    300,
  )
  const loadArtistOptions = useCallback(
    (
      input: string,
      callback: (error: Error | null | undefined, result: any) => void,
    ) => {
      if (!input) {
        callback(null, [])
      }

      getArtistOptions(input, callback)
    },
    [getArtistOptions],
  )
  const updateRelatedArtists = useCallback(
    options => {
      const update = {
        related_artist_ids: options.map(({ value }) => value),
      }
      dispatch(updateArtist(artist.id, update))
      dispatch(sendMixpanelEvent('Manage Artist modal - Link artist'))
    },
    [artist.id, updateArtist],
  )
  return (
    <>
      <Heading size={500}>Link other {typeData} pages</Heading>
      <Row columnsSize={[12]}>
        <div>
          <Label caption={`Select ${typeData}`} />
          <CustomDropdown
            async={true}
            disabled={artist.loading}
            loadOptions={loadArtistOptions}
            multi={true}
            placeholder={artist.loading ? 'Loading...' : 'Type to search'}
            value={relatedArtistsDropdownOptions}
            onChange={updateRelatedArtists}
          />
        </div>
      </Row>
      <div className="form-note">
        Here you can link multiple {typeData}s so their tracks appear together
        on a unified {typeData} page. This is designed for {typeData}s who go by
        multiple aliases or are associated to multiple bands/{typeData}{' '}
        projects, i.e. Thom Yorke and Radiohead.
        <br />
        <br />
        Once you link a child {typeData} to a parent {typeData} profile, the
        child {typeData} profile will no longer have its own {typeData} profile
        visible on your Catalogs. Track metadata will still retain the child{' '}
        {typeData} name, but clicking on the child {typeData} will take the user
        to the parent {typeData} profile.
      </div>
      {/* TODO: Fix CustomDropdown inside overflow container */}
      <div
        style={{
          height: 300,
        }}
      />
    </>
  )
}

export default ArtistRelatedArtists
